import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Switch,
  Tooltip,
  Avatar,
  Chip,
  Box,
  Typography
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  Person as PersonIcon
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { format, parseISO } from "date-fns";
import { useTitle } from "../../hooks/useTitle";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.background.default,
  },
  tablePaper: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: "1px solid " + theme.palette.divider,
  },
  searchInput: {
    flex: 1,
    minWidth: 250,
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: 32,
    height: 32,
    fontSize: "0.875rem",
    marginRight: theme.spacing(1.5),
  },
  patientNameCell: {
    display: "flex",
    alignItems: "center",
    fontWeight: 500,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8),
    color: theme.palette.text.secondary,
  }
}));

const MedicalHistoryIndex = () => {
  useTitle("Lista de Pacientes");
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [count, setCount] = useState(0);
  const isDoctorOnly = user?.isDoctor && user?.profile !== "admin";
  const [onlyMyPatients, setOnlyMyPatients] = useState(false);

  useEffect(() => {
    if (isDoctorOnly) {
      setOnlyMyPatients(true);
    }
  }, [isDoctorOnly]);
  
  const fetchPatients = async () => {
    setLoading(true);
    try {
      const params = {
        searchParam,
        pageNumber,
      };

      if (onlyMyPatients || isDoctorOnly) {
        params.doctorId = user.id;
      }

      const { data } = await api.get("/medical-history/patients", { params });
      setPatients(data.patients);
      setCount(data.count);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchPatients();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, onlyMyPatients]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value);
    setPageNumber(1);
  };

  const handleOpenPatient = (patient) => {
    let url = "/medical-history/" + patient.contact.id;

    if (patient.patientName) {
      url += "?patientName=" + encodeURIComponent(patient.patientName);
    }

    history.push(url);
  };

  const getInitials = (name) => {
    return name
      .replace(/\s+/, " ")
      .split(" ")
      .slice(0, 2)
      .map((v) => v && v[0].toUpperCase())
      .join("");
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Historial Médico</Title>
        <MainHeaderButtonsWrapper />
      </MainHeader>

      <Paper className={classes.mainPaper} elevation={0}>
        <div className={classes.searchContainer}>
            <TextField
                className={classes.searchInput}
                placeholder="Buscar paciente..."
                type="search"
                variant="outlined"
                size="small"
                value={searchParam}
                onChange={handleSearch}
                InputProps={{
                    startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon color="action" />
                    </InputAdornment>
                    ),
                }}
            />
            {(user.profile === "admin" || user.profile === "superadmin") && user.isDoctor && (
                <FormControlLabel
                    control={
                        <Switch
                            checked={onlyMyPatients}
                            onChange={(e) => {
                                setOnlyMyPatients(e.target.checked);
                                setPageNumber(1);
                            }}
                            name="onlyMyPatients"
                            color="primary"
                        />
                    }
                    label="Ver solo mis pacientes"
                />
            )}
        </div>

        <Paper className={classes.tablePaper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Paciente</TableCell>
                <TableCell align="center">Historial</TableCell>
                <TableCell align="center">Última Visita</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRowSkeleton columns={4} />
              ) : (
                <>
                  {patients.length > 0 ? (
                    patients.map((p, index) => {
                        const contactName = p.contact.name || "Desconocido";
                        let mainName = contactName;
                        let subName = "";
                        
                        if (p.patientName) {
                            mainName = p.patientName;
                            subName = "Contacto: " + contactName;
                        }

                        return (
                            <TableRow 
                                key={index} 
                                hover 
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOpenPatient(p)}
                            >
                                <TableCell>
                                  <div className={classes.patientNameCell}>
                                    <Avatar className={classes.avatar}>{getInitials(mainName)}</Avatar>
                                    <div>
                                      <Typography variant="body2" style={{ fontWeight: 600 }}>{mainName}</Typography>
                                      {subName && (
                                        <Typography variant="caption" color="textSecondary">{subName}</Typography>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={(p.appointmentsCount || 0) + " citas"}
                                    size="small"
                                    style={{ backgroundColor: p.appointmentsCount > 0 ? "#e3f2fd" : "#f5f5f5", color: p.appointmentsCount > 0 ? "#1976d2" : "#757575", fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2">
                                    {p.lastAppointmentDate 
                                      ? (() => {
                                          const [yyyy, mm, dd] = p.lastAppointmentDate.split("-");

                                          const dateObj = new Date(yyyy, mm - 1, dd);

                                          return dateObj.toLocaleDateString(undefined, { 
                                            day: "numeric", 
                                            month: "long", 
                                            year: "numeric" 
                                          });
                                        })()
                                      : "—"
                                    }
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Ver Expediente Completo">
                                        <IconButton size="small" onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenPatient(p);
                                        }} color="primary">
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <div className={classes.emptyState}>
                          <PersonIcon style={{ fontSize: 60, opacity: 0.2, marginBottom: 16 }} />
                          <Typography variant="h6" color="textSecondary">No se encontraron pacientes</Typography>
                          <Typography variant="body2" color="textSecondary">Intenta ajustar los filtros de búsqueda.</Typography>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>

          {patients.length > 0 && (
            <Box display="flex" justifyContent="center" p={2}>
                <Pagination
                    count={Math.ceil(count / 20)}
                    page={pageNumber}
                    onChange={(e, v) => setPageNumber(v)}
                    color="primary"
                    showFirstButton 
                    showLastButton
                />
            </Box>
          )}
        </Paper>
      </Paper>
    </MainContainer>
  );
};

export default MedicalHistoryIndex;